import React, { useEffect, useMemo, useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { useAuth } from '../../../auth/components/AuthProvider';
import {
  computeVerificacionContactos,
  normalizeEmpresaContactos,
} from '../../../../utils/empresaContactosVerification';

const METODOS = ['correo', 'llamada', 'whatsapp', 'manual'];

function toDigits(v) {
  return String(v || '').replace(/\D/g, '');
}

function toWhatsAppE164(raw) {
  const d = toDigits(raw);
  if (!d) return null;
  // Ya viene con 56
  if (d.startsWith('56') && d.length >= 10) return d;
  // Celular Chile: 9xxxxxxxx (9 dígitos) => 56 + d
  if (d.length === 9 && d.startsWith('9')) return `56${d}`;
  // 8 dígitos (sin 9) => asumir celular y anteponer 569
  if (d.length === 8) return `569${d}`;
  // Fijo con código de área (ej 221234567 => 56 + d)
  if (d.length === 9 && d.startsWith('2')) return `56${d}`;
  // Fallback: devolver dígitos tal cual
  return d;
}

function buildVerificationText({ empresaId, empresaNombre, contactoLabel, contactoValor, adminLabel }) {
  const emp = (empresaNombre || 'su empresa').toString().trim();
  const admin = (adminLabel || '').toString().trim();
  const who = admin ? `Hola! Soy ${admin}, ` : 'Hola! ';
  const val = (contactoValor || '').toString().trim();
  const baseOrigin = 'https://kawescar.cl';
  const profileUrl = `${baseOrigin}/empresa/${empresaId || ''}`;
  return `${who}le escribo desde kawescar.cl la vitrina digital del sector automotriz. Te escribimos para verificar los datos de: ${emp}. ¿Nos confirma si este ${contactoLabel} es correcto?\n\n${contactoLabel}: ${val}\n\nAccede a tu perfil:\n${profileUrl}`.trim();
}

function MetodoButtons({ value, disabled, onChange }) {
  return (
    <div className={`flex flex-wrap gap-2 ${disabled ? 'opacity-50' : ''}`}>
      {METODOS.map((m) => {
        const active = value === m;
        return (
          <button
            key={m}
            type="button"
            disabled={disabled}
            onClick={() => onChange(m)}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${
              active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {m}
          </button>
        );
      })}
    </div>
  );
}

function ContactRow({
  title,
  help,
  block,
  onChangeValor,
  onChangeVerified,
  onChangeMetodo,
  onActionCorreo,
  onActionWhatsApp,
  onActionLlamada,
  showCorreoAction = false,
  showWhatsAppAction = false,
  showLlamadaAction = false,
}) {
  const hasValue = !!(block?.valor || '').trim();
  const checked = !!block?.verificado;
  return (
    <div className="border border-gray-200 rounded-xl p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-gray-900">{title}</p>
            {checked ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                Verificado
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                No verificado
              </span>
            )}
          </div>
          {help ? <p className="text-xs text-gray-500 mt-1">{help}</p> : null}
        </div>
        <label className={`flex items-center gap-2 text-sm font-medium ${hasValue ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
          <input
            type="checkbox"
            className="rounded border-gray-300"
            checked={checked}
            disabled={!hasValue}
            onChange={(e) => onChangeVerified(e.target.checked)}
          />
          Verificar
        </label>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Valor</label>
          <input
            type="text"
            value={(block?.valor || '').trim()}
            onChange={(e) => onChangeValor?.(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-800"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Método</label>
          <MetodoButtons
            value={block?.metodo || 'manual'}
            disabled={!hasValue || !checked}
            onChange={onChangeMetodo}
          />
          {!checked ? <p className="text-[11px] text-gray-500 mt-1">Marca “Verificar” para seleccionar método.</p> : null}
        </div>
      </div>

      {(showCorreoAction || showWhatsAppAction || showLlamadaAction) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {showCorreoAction && (
            <button
              type="button"
              disabled={!hasValue}
              onClick={onActionCorreo}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
            >
              📧 Enviar correo
            </button>
          )}
          {showWhatsAppAction && (
            <button
              type="button"
              disabled={!hasValue}
              onClick={onActionWhatsApp}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
            >
              💬 Abrir WhatsApp
            </button>
          )}
          {showLlamadaAction && (
            <button
              type="button"
              disabled={!hasValue}
              onClick={onActionLlamada}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
            >
              📞 Llamar
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function ContactVerificationModal({ empresa, isOpen, onClose, onSaved }) {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [observacionGeneral, setObservacionGeneral] = useState('');

  const initial = useMemo(() => normalizeEmpresaContactos(empresa), [empresa]);
  const [contactos, setContactos] = useState(initial);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setObservacionGeneral('');
    setContactos(normalizeEmpresaContactos(empresa));
  }, [isOpen, empresa]);

  const derived = useMemo(() => computeVerificacionContactos(contactos), [contactos]);

  const setContacto = (key, patch) => {
    setContactos((prev) => ({
      ...prev,
      [key]: { ...(prev?.[key] || {}), ...(patch || {}) },
    }));
  };

  const handleToggle = (key, nextChecked) => {
    const block = contactos?.[key] || {};
    const hasValue = !!(block?.valor || '').trim();
    if (!hasValue && nextChecked) return;
    if (nextChecked) {
      setContacto(key, {
        verificado: true,
        // metadata final se fija al guardar (serverTimestamp), pero dejamos flags/método listos
        metodo: block.metodo ?? 'manual',
      });
    } else {
      setContacto(key, {
        verificado: false,
        fechaVerificacion: null,
        verificadoPor: null,
        metodo: null,
      });
    }
  };

  const empresaNombre = (empresa?.nombre || empresa?.nombre_empresa || '').toString().trim();
  const agenteEmail =
    empresa?.agenteAsignado?.email ||
    empresa?.agenteAsignado?.agenteEmail ||
    empresa?.agenteEmail ||
    empresa?.agente_email ||
    empresa?.agente_asignado?.email ||
    empresa?.agente_asignado?.agenteEmail ||
    '';
  const adminLabel = (agenteEmail || '').toString().trim();

  const openMailto = (correo) => {
    const subject = `Verificación de contacto - Kawescar`;
    const body = buildVerificationText({
      empresaId: empresa?.id,
      empresaNombre,
      contactoLabel: 'correo',
      contactoValor: correo,
      adminLabel,
    });
    window.open(`mailto:${encodeURIComponent(correo)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank', 'noopener,noreferrer');
  };

  const openWhatsApp = (phone, label) => {
    const e164 = toWhatsAppE164(phone);
    if (!e164) return;
    const text = buildVerificationText({
      empresaId: empresa?.id,
      empresaNombre,
      contactoLabel: label,
      contactoValor: phone,
      adminLabel,
    });
    window.open(`https://wa.me/${encodeURIComponent(e164)}?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  const openTel = (phone) => {
    const digits = toDigits(phone);
    if (!digits) return;
    window.open(`tel:${digits}`, '_self');
  };

  const save = async () => {
    if (!empresa?.id) return;
    setSaving(true);
    setError(null);
    try {
      const verificadoPor = user?.email || user?.uid || null;

      const next = { ...contactos };
      for (const k of ['correo', 'telefonoFijo', 'whatsapp', 'telefonoMovil']) {
        const b = next[k] || {};
        const hasValue = !!(b?.valor || '').trim();
        if (b.verificado === true && !hasValue) {
          next[k] = { ...b, verificado: false, fechaVerificacion: null, verificadoPor: null, metodo: null };
        }
      }

      const payload = {
        contactos: {
          ...next,
          correo:
            next.correo?.verificado === true
              ? { ...next.correo, verificado: true, fechaVerificacion: serverTimestamp(), verificadoPor, metodo: next.correo.metodo || 'correo' }
              : { ...next.correo, verificado: false, fechaVerificacion: null, verificadoPor: null, metodo: null },
          telefonoFijo:
            next.telefonoFijo?.verificado === true
              ? { ...next.telefonoFijo, verificado: true, fechaVerificacion: serverTimestamp(), verificadoPor, metodo: next.telefonoFijo.metodo || 'llamada' }
              : { ...next.telefonoFijo, verificado: false, fechaVerificacion: null, verificadoPor: null, metodo: null },
          whatsapp:
            next.whatsapp?.verificado === true
              ? { ...next.whatsapp, verificado: true, fechaVerificacion: serverTimestamp(), verificadoPor, metodo: next.whatsapp.metodo || 'whatsapp' }
              : { ...next.whatsapp, verificado: false, fechaVerificacion: null, verificadoPor: null, metodo: null },
          telefonoMovil:
            next.telefonoMovil?.verificado === true
              ? { ...next.telefonoMovil, verificado: true, fechaVerificacion: serverTimestamp(), verificadoPor, metodo: next.telefonoMovil.metodo || 'llamada' }
              : { ...next.telefonoMovil, verificado: false, fechaVerificacion: null, verificadoPor: null, metodo: null },
        },
        verificacionContactos: {
          ...computeVerificacionContactos(next),
          empresaVerificada: computeVerificacionContactos(next).tieneTelefonoVerificado,
          tieneTelefonoVerificado: computeVerificacionContactos(next).tieneTelefonoVerificado,
          tieneCorreoVerificado: computeVerificacionContactos(next).tieneCorreoVerificado,
          fechaActualizacion: serverTimestamp(),
          verificadoPor,
          observacionGeneral: observacionGeneral ? String(observacionGeneral).trim() : null,
        },
        // Compatibilidad con campos legacy usados en distintas vistas
        email: (next?.correo?.valor || '').trim() || null,
        telefono_fijo: (next?.telefonoFijo?.valor || '').trim() || null,
        whatsapp: (next?.whatsapp?.valor || '').trim() || null,
        wsap: (next?.whatsapp?.valor || '').trim() || null,
      };

      await updateDoc(doc(db, 'empresas', empresa.id), payload);
      onSaved?.(payload);
      onClose?.();
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Error al guardar verificación');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-xl font-bold text-gray-900 truncate">Verificación de contactos</h3>
              <p className="text-sm text-gray-600 mt-1 truncate">{empresa?.nombre || empresa?.nombre_empresa || empresa?.id || 'Empresa'}</p>
              <p className="text-xs text-gray-500 mt-2">
                La empresa se mostrará como verificada públicamente cuando al menos un teléfono, WhatsApp o teléfono fijo esté confirmado.
              </p>
            </div>
            <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg hover:bg-gray-50 text-gray-600">
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex flex-wrap gap-2">
            {derived.empresaVerificada ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                ✓ Contacto verificado
              </span>
            ) : (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-amber-50 text-amber-800">
                ⏳ Contacto pendiente
              </span>
            )}
            {derived.tieneCorreoVerificado ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-sky-50 text-sky-800">
                ✓ Correo verificado (no activa badge público)
              </span>
            ) : null}
          </div>

          <ContactRow
            title="Correo"
            help="El correo puede verificarse, pero NO activa el badge público."
            block={contactos.correo}
            onChangeValor={(v) => setContacto('correo', { valor: v })}
            onChangeVerified={(v) => handleToggle('correo', v)}
            onChangeMetodo={(m) => setContacto('correo', { metodo: m })}
            showCorreoAction
            onActionCorreo={() => {
              const v = (contactos?.correo?.valor || '').trim();
              if (!v) return;
              setContacto('correo', { metodo: 'correo' });
              openMailto(v);
            }}
          />
          <ContactRow
            title="Teléfono fijo"
            help="Activa verificación pública si está confirmado."
            block={contactos.telefonoFijo}
            onChangeValor={(v) => setContacto('telefonoFijo', { valor: v })}
            onChangeVerified={(v) => handleToggle('telefonoFijo', v)}
            onChangeMetodo={(m) => setContacto('telefonoFijo', { metodo: m })}
            showLlamadaAction
            onActionLlamada={() => {
              const v = (contactos?.telefonoFijo?.valor || '').trim();
              if (!v) return;
              setContacto('telefonoFijo', { metodo: 'llamada' });
              openTel(v);
            }}
          />
          <ContactRow
            title="WhatsApp"
            help="Activa verificación pública si está confirmado."
            block={contactos.whatsapp}
            onChangeValor={(v) => setContacto('whatsapp', { valor: v })}
            onChangeVerified={(v) => handleToggle('whatsapp', v)}
            onChangeMetodo={(m) => setContacto('whatsapp', { metodo: m })}
            showWhatsAppAction
            onActionWhatsApp={() => {
              const v = (contactos?.whatsapp?.valor || '').trim();
              if (!v) return;
              setContacto('whatsapp', { metodo: 'whatsapp' });
              openWhatsApp(v, 'whatsapp');
            }}
          />

          <div className="border border-gray-200 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Observación (opcional)</label>
            <textarea
              value={observacionGeneral}
              onChange={(e) => setObservacionGeneral(e.target.value)}
              rows={2}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="Ej: Confirmado por llamada el 26/03"
            />
            <p className="text-xs text-gray-500 mt-2">
              Admin: {user?.email || user?.uid || '—'} · Se guardará fecha de verificación automáticamente al confirmar.
            </p>
          </div>

          {error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">{error}</div>
          ) : null}
        </div>

        <div className="p-6 pt-0 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            Cancelar
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-70"
          >
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

